<template>
  <div class="container page-global">
    <section class="content">
      <h1>Privacy Policy</h1>
      <p class="effective-date">Effective as of: March 4, 2026</p>

      <p>
        This Privacy Policy explains how Cashless FYI, operated by Newcare
        International, LLC ("Newcare", "we", "us", or "our"), collects, uses,
        and protects information in connection with our ROI calculators and
        related content (the "Service").
      </p>

      <h2>1. Information We Collect</h2>
      <p>
        We design Cashless FYI so you can explore financial scenarios with as
        little friction as possible. Depending on how you use the Service, we
        may collect:
      </p>
      <ul>
        <li>
          <strong>Calculator Inputs.</strong> Numbers and assumptions you enter
          into the calculators (for example, sales, hours, wages, or volumes).
          These values are used to generate projections and insights.
        </li>
        <li>
          <strong>Basic Contact Information.</strong> If you choose to contact
          us or request follow‑up, we may collect your name, email address, and
          any other details you choose to provide.
        </li>
        <li>
          <strong>Technical and Usage Data.</strong> Standard log and device
          information (such as IP address, browser type, and pages visited)
          that helps us keep the Service secure and understand aggregate usage
          patterns.
        </li>
      </ul>

      <h2>2. How We Use Information</h2>
      <p>We use the information we collect to:</p>
      <ul>
        <li>Provide and operate the calculators and related content;</li>
        <li>
          Improve, troubleshoot, and refine the accuracy and usability of the
          Service;
        </li>
        <li>
          Respond to inquiries and communicate with you about the Service when
          you ask us to;
        </li>
        <li>
          Maintain the security and integrity of the Service and comply with
          legal obligations.
        </li>
      </ul>

      <h2>3. How We Do <em>Not</em> Use or Share Information</h2>
      <p>
        We treat the information we handle through Cashless FYI as something we
        are stewards of, not something we sell.
      </p>
      <ul>
        <li>
          <strong>No selling of your personal data.</strong> We do not sell
          your personal information to third parties.
        </li>
        <li>
          <strong>No sharing for third‑party marketing.</strong> We do not
          share your personal information with third parties for their own
          independent advertising or marketing purposes.
        </li>
        <li>
          <strong>Limited disclosures.</strong> We may share information only:
          <ul>
            <li>
              With service providers who assist us in operating the Service and
              who are bound by confidentiality obligations;
            </li>
            <li>
              As required by law, regulation, legal process, or enforceable
              governmental request;
            </li>
            <li>
              To protect the rights, property, or safety of Newcare, our users,
              or the public, as permitted by law.
            </li>
          </ul>
        </li>
      </ul>

      <h2>4. Data Stewardship and Security</h2>
      <p>
        We use reasonable organizational, technical, and administrative
        measures designed to protect the information we handle from
        unauthorized access, loss, misuse, or alteration. No online system can
        be guaranteed to be 100% secure, and you should take care in how you
        transmit information to us and retain copies of any important inputs or
        results for your own records.
      </p>

      <h2>5. Retention</h2>
      <p>
        We retain information only for as long as reasonably necessary to
        provide the Service, to improve and secure it, to resolve disputes, or
        to comply with our legal obligations. Where possible, we prefer to
        work with aggregated or de‑identified information for analytics rather
        than data that directly identifies you.
      </p>

      <h2>6. Cookies and Similar Technologies</h2>
      <p>
        The Service may use cookies or similar technologies to maintain session
        state, remember preferences, and understand how the site is used in the
        aggregate. You can control cookies through your browser settings; if
        you disable them, some features of the Service may not function
        properly.
      </p>

      <h2>7. Your Choices</h2>
      <p>You have choices about how you use Cashless FYI:</p>
      <ul>
        <li>
          You can choose what information to enter into the calculators and may
          clear or regenerate results at any time.
        </li>
        <li>
          If you contact us and later wish to update or delete your contact
          information, you can do so by reaching out using the contact details
          on the site.
        </li>
      </ul>

      <h2>8. Children</h2>
      <p>
        The Service is intended for use by adults in a business or professional
        context and is not directed to children under the age of 13. We do not
        knowingly collect personal information from children under 13.
      </p>

      <h2>9. Changes to This Policy</h2>
      <p>
        We may update this Privacy Policy from time to time. When we do, we
        will revise the "Effective as of" date above and may provide additional
        notice (for example, via the site footer). Your continued use of the
        Service after an update means you accept the revised Policy.
      </p>

      <h2>10. Contact Us</h2>
      <p>
        If you have questions about this Privacy Policy or how we handle
        information, please contact us using the contact details provided on
        the site.
      </p>
    </section>
  </div>
</template>

<script>
export default {
  name: "Privacy",
};
</script>

<style lang="scss" scoped>
@import '@/assets/css/app.scss';

.content {
  padding: 4rem 0;
  text-align: left;
}

h1 {
  font-size: 2.5rem;
  font-weight: 800;
  color: $body-color;
  margin-bottom: 0.5rem;
  text-align: center;
}

.effective-date {
  text-align: center;
  font-size: 0.9rem;
  color: $secondary;
  margin-bottom: 2rem;
}

h2 {
  font-size: 1.35rem;
  font-weight: 700;
  margin-top: 1.75rem;
  margin-bottom: 0.75rem;
}

p {
  margin-bottom: 0.85rem;
  line-height: 1.6;
}

ul {
  margin: 0 0 0.85rem 1.25rem;
  padding: 0;

  li {
    margin-bottom: 0.35rem;
  }
}
</style>

